
import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Space, Input, message, Modal, Form,
    Typography, Tag, Divider, Empty, Tooltip, Switch
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
    SearchOutlined, LinkOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dnsService from '../../services/dnsService';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';

import { userPath } from '../../../shared/config/paths';

const { Title } = Typography;
const { confirm } = Modal;

const DnsCidr = () => {
    const [cidrs, setCidrs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCidr, setEditingCidr] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const navigate = useNavigate();

    // 获取CIDR列表
    const fetchCidrs = async () => {
        setLoading(true);
        try {
            const result = await dnsService.getCidrs(searchValue);
            if (result.success) {
                setCidrs(result.data);
            } else {
                message.error(result.error || '获取CIDR失败');
            }
        } catch (error) {
            message.error('获取CIDR失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCidrs();
    }, [searchValue]);

    // 显示添加CIDR模态框
    const showAddModal = () => {
        setEditingCidr(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    // 显示编辑CIDR模态框
    const showEditModal = (cidr) => {
        setEditingCidr(cidr);
        form.setFieldsValue({
            name: cidr.name,
            description: cidr.description || '',
            is_public: cidr.is_public ? true : false,
            i18n: parseI18n(cidr.i18n)
        });
        setIsModalVisible(true);
    };

    // 处理模态框确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const submitData = {
                ...values,
                is_public: values.is_public ? 1 : 0,
                i18n: cleanI18n(mergeI18n(editingCidr?.i18n, values.i18n))
            };
            setLoading(true);

            if (editingCidr) {
                // 更新CIDR
                const result = await dnsService.updateCidr({
                    id: editingCidr.id,
                    ...submitData
                });

                if (result.success) {
                    message.success('CIDR更新成功');
                    setIsModalVisible(false);
                    fetchCidrs();
                } else {
                    message.error(result.error || '更新CIDR失败');
                }
            } else {
                // 创建新CIDR
                const result = await dnsService.createCidr(submitData);

                if (result.success) {
                    message.success('CIDR创建成功');
                    setIsModalVisible(false);
                    fetchCidrs();
                } else {
                    message.error(result.error || '创建CIDR失败');
                }
            }
        } catch (error) {
            console.log('Validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // 删除CIDR
    const deleteCidr = (cidr) => {
        confirm({
            title: '确认删除',
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除CIDR "${cidr.name}" 吗？此操作将同时删除所有关联的IP范围，无法撤销。`,
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await dnsService.deleteCidr(cidr.id);

                    if (result.success) {
                        message.success('CIDR删除成功');
                        fetchCidrs();
                    } else {
                        message.error(result.error || '删除CIDR失败');
                    }
                } catch (error) {
                    message.error('删除CIDR失败: ' + error.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 处理搜索
    const handleSearch = (value) => {
        setSearchValue(value);
    };

    // 表格列配置
    const columns = [
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            render: text => <a>{text}</a>,
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            render: text => text || '-',
            ellipsis: true,
        },
        {
            title: '公开',
            dataIndex: 'is_public',
            key: 'is_public',
            width: 80,
            render: value => value ? <Tag color="green">公开</Tag> : <Tag color="default">私有</Tag>,
        },
        {
            title: 'IP条目数',
            dataIndex: 'entry_count',
            key: 'entry_count',
            render: count => {
                if (!count) return 0;
                return (
                    <Tooltip title="查看详情">
                        <Tag color="blue" style={{ cursor: 'pointer' }}>{count} 个IP范围</Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: '使用状态',
            key: 'usage',
            render: (_, record) => {
                const count = record.dns_usage_count || 0;
                if (count === 0) return <Tag color="default">未使用</Tag>;
                return <Tag color="green">{count} 条记录使用中</Tag>;
            }
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => navigate(userPath(`/dns/cidr/${record.id}`))}
                        icon={<LinkOutlined />}
                    >
                        管理IP范围
                    </Button>
                    <Button
                        type="default"
                        size="small"
                        onClick={() => showEditModal(record)}
                        icon={<EditOutlined />}
                    >
                        编辑
                    </Button>
                    <Button
                        type="default"
                        danger
                        size="small"
                        onClick={() => deleteCidr(record)}
                        icon={<DeleteOutlined />}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>CIDR管理</Title>

            <Card
                title={
                    <Space size="middle">
                        <span>CIDR列表</span>
                        <Tooltip title="CIDR组用于将IP地址分组，可以在DNS记录中引用，实现基于IP的智能解析">
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={showAddModal}
                    >
                        添加CIDR
                    </Button>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Input.Search
                        placeholder="搜索CIDR名称或描述"
                        allowClear
                        enterButton={<Button icon={<SearchOutlined />}>搜索</Button>}
                        onSearch={handleSearch}
                        style={{ maxWidth: 400 }}
                    />
                </div>

                <Table
                    columns={columns}
                    dataSource={cidrs}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: <Empty description="暂无CIDR组" />
                    }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: total => `共 ${total} 个CIDR组`
                    }}
                    onRow={(record) => ({
                        onClick: () => navigate(userPath(`/dns/cidr/${record.id}`))
                    })}
                />
            </Card>

            {/* 添加/编辑CIDR模态框 */}
            <Modal
                title={editingCidr ? `编辑CIDR: ${editingCidr.name}` : '添加CIDR'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: t('Name') }, { key: 'description', label: t('Description'), type: 'textarea' }]}
                        base={<>
                    <Form.Item
                        name="name"
                        label="CIDR名称"
                        rules={[{ required: true, message: '请输入CIDR名称!' }]}
                    >
                        <Input placeholder="例如: 中国电信" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="描述 (可选)"
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="例如: 中国电信IP地址范围，用于中国电信用户的解析"
                        />
                    </Form.Item>
                        </>}
                    />

                    <Form.Item
                        name="is_public"
                        label="公开"
                        valuePropName="checked"
                        tooltip="公开的CIDR对所有用户可见，仅管理员可设置"
                    >
                        <Switch checkedChildren="公开" unCheckedChildren="私有" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DnsCidr;
